import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/config";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, CheckCircle2, CalendarClock, MapPin, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function BookingConfirm() {
  const { bookingId } = useParams();
  const [search] = useSearchParams();
  const token = search.get("token") || "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [showReschedule, setShowReschedule] = useState(false);
  const [requestedDate, setRequestedDate] = useState("");
  const [requestedTime, setRequestedTime] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/bookings/${bookingId}/confirmation`, { params: { token } });
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Confirmation link is invalid or expired.");
      } finally { setLoading(false); }
    })();
  }, [bookingId, token]);

  const submit = async (action) => {
    setSubmitting(action);
    try {
      const { data: out } = await api.post(`/bookings/${bookingId}/confirmation`, {
        token,
        action,
        requested_date: requestedDate || null,
        requested_time: requestedTime || null,
        note: note || null,
      });
      if (action === "confirm") {
        setData((d) => d ? { ...d, customer_confirmed_at: new Date().toISOString(), status: "confirmed" } : d);
        toast.success("Appointment confirmed. See you soon!");
      } else {
        setData((d) => d ? { ...d, reschedule_requested_at: new Date().toISOString() } : d);
        toast.success("Reschedule request sent. We'll follow up with new options.");
      }
      setShowReschedule(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally { setSubmitting(null); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#047857]" /></div>;
  if (error || !data) return (
    <div className="max-w-md mx-auto px-4 py-16 text-center">
      <h1 className="text-2xl font-extrabold tracking-tighter font-[Outfit] text-gray-900">{error || "Not found"}</h1>
      <Button onClick={() => window.location.assign("/")} className="mt-6 rounded-full">Go home</Button>
    </div>
  );

  const confirmed = !!data.customer_confirmed_at;
  const rescheduled = !!data.reschedule_requested_at;

  return (
    <div className="max-w-md mx-auto px-4 py-10" data-testid="booking-confirm-page">
      <button onClick={() => window.history.back()} className="flex items-center gap-1 text-sm font-bold text-gray-600 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="bg-white rounded-3xl border border-gray-200 p-7 shadow-sm">
        <div className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-emerald-700">Your appointment</div>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tighter font-[Outfit] text-gray-900" data-testid="booking-confirm-service">{data.service_name}</h1>
        {Array.isArray(data.additional_services) && data.additional_services.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1" data-testid="booking-confirm-addons">
            {data.additional_services.map((a, i) => (
              <span key={i} className="inline-flex items-center px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.1em] rounded-full bg-emerald-50 text-[#047857] border border-emerald-200">
                + {a.service_name}
              </span>
            ))}
          </div>
        )}
        <div className="mt-3 flex items-center gap-2 text-gray-700">
          <CalendarClock className="w-4 h-4 text-[#047857]" />
          <div className="text-sm font-bold">{data.preferred_date} · {data.preferred_time}</div>
        </div>
        {data.address && (
          <div className="mt-1 flex items-center gap-2 text-gray-700">
            <MapPin className="w-4 h-4 text-[#047857]" />
            <div className="text-sm">{data.address}</div>
          </div>
        )}
        {data.price != null && <div className="mt-3 text-xs text-gray-500">Quoted ${data.price.toFixed(2)}</div>}

        {confirmed ? (
          <div className="mt-5 rounded-2xl bg-emerald-50 border-2 border-emerald-200 p-4" data-testid="booking-confirm-confirmed">
            <div className="flex items-center gap-2 text-emerald-800 font-extrabold">
              <CheckCircle2 className="w-5 h-5" /> Confirmed — see you {data.preferred_date}
            </div>
            <div className="mt-1 text-xs text-emerald-900/80">We've let our crew know. We'll send a same-day reminder when we're on the way.</div>
          </div>
        ) : rescheduled ? (
          <div className="mt-5 rounded-2xl bg-amber-50 border-2 border-amber-200 p-4" data-testid="booking-confirm-rescheduled">
            <div className="flex items-center gap-2 text-amber-800 font-extrabold">⏳ Reschedule pending</div>
            <div className="mt-1 text-xs text-amber-900/80">We'll text + email you with new options within a few hours.</div>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <Button
              onClick={() => submit("confirm")}
              disabled={!!submitting}
              data-testid="booking-confirm-yes"
              className="rounded-2xl py-6 bg-gradient-to-br from-[#0A2E1E] to-[#062818] hover:opacity-90 text-white font-extrabold"
            >
              {submitting === "confirm" ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-5 h-5 mr-2" /> I'll be there</>}
            </Button>
            <Button
              onClick={() => setShowReschedule(true)}
              disabled={!!submitting}
              variant="outline"
              data-testid="booking-confirm-reschedule"
              className="rounded-2xl py-6 border-2 font-extrabold"
            >
              Request reschedule
            </Button>
          </div>
        )}

        {showReschedule && !rescheduled && !confirmed && (
          <div className="mt-5 rounded-2xl bg-gray-50 border border-gray-200 p-4 space-y-3" data-testid="reschedule-form">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-600">Preferred date</Label>
              <Input type="date" value={requestedDate} onChange={(e) => setRequestedDate(e.target.value)} data-testid="reschedule-date" className="mt-1" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-600">Preferred time</Label>
              <Input placeholder="e.g. Morning, 9 AM" value={requestedTime} onChange={(e) => setRequestedTime(e.target.value)} data-testid="reschedule-time" className="mt-1" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-600">Note (optional)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything we should know?" data-testid="reschedule-note" className="mt-1" />
            </div>
            <Button
              onClick={() => submit("reschedule_request")}
              disabled={!!submitting}
              data-testid="reschedule-submit"
              className="w-full rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold"
            >
              {submitting === "reschedule_request" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send request"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
